import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CalendarDays, Clock } from 'lucide-react';
import { ReaderAvatar } from '@/components/brand';
import type { Reader } from '@/types/reader';

const STATIC_ROOT = 'https://casting.heyjoe.io/api/s3/';

interface ReaderDetailDialogProps {
  open: boolean;
  onClose: () => void;
  reader: Reader | null;
  onInvite: (reader: Reader) => void;
}

function formatServiceType(type?: string): string {
  if (!type) return '';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatRate(rate?: number): string {
  return rate != null ? `$${rate.toFixed(2)}` : '--';
}

export const ReaderDetailDialog: React.FC<ReaderDetailDialogProps> = ({
  open,
  onClose,
  reader,
  onInvite,
}) => {
  if (!reader) return null;
  const profile = reader.freelancer_profile;
  const avatarUrl = reader.logo ? `${STATIC_ROOT}${reader.logo}` : null;

  const fullName = `${reader.first_name || ''} ${reader.last_name || ''}`.trim();
  const sectionStyle: React.CSSProperties = {
    padding: 14,
    borderRadius: 'var(--hj-r-sm)',
    background: 'var(--hj-beige-deep)',
    border: '1px solid var(--hj-beige-line)',
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="sm:max-w-lg max-h-[85vh] overflow-y-auto"
        data-paper="beige"
        style={{
          background: 'var(--hj-beige)',
          border: '1px solid var(--hj-beige-line)',
          color: 'var(--hj-beige-ink)',
        }}
      >
        <DialogHeader>
          <DialogTitle
            className="font-hj-display"
            style={{ fontSize: 24, color: 'var(--hj-beige-ink)', letterSpacing: '-0.01em' }}
          >
            {fullName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col" style={{ gap: 18 }}>
          {/* Avatar + Basic Info */}
          <div className="flex items-start gap-4">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="hj-headshot object-cover flex-shrink-0"
                style={{ width: 72, height: 72 }}
              />
            ) : (
              <ReaderAvatar name={fullName || 'Reader'} size={72} />
            )}
            <div className="flex-1 flex flex-col gap-2">
              {profile?.timezone && (
                <div
                  className="inline-flex items-center gap-1.5"
                  style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-3)' }}
                >
                  <Clock size={12} />
                  {profile.timezone}
                </div>
              )}
              <div className="flex flex-col gap-1">
                <div className="mono-label">Rates · per 15 min</div>
                {profile?.reader_rate != null && (
                  <div className="flex justify-between" style={{ fontSize: 13 }}>
                    <span style={{ color: 'var(--hj-beige-ink-3)' }}>Reader</span>
                    <span className="tabular" style={{ color: 'var(--hj-beige-ink)', fontWeight: 600, fontFamily: 'var(--hj-font-mono)' }}>
                      {formatRate(profile.reader_rate)}
                    </span>
                  </div>
                )}
                {profile?.reader_director_rate != null && (
                  <div className="flex justify-between" style={{ fontSize: 13 }}>
                    <span style={{ color: 'var(--hj-beige-ink-3)' }}>Session Director</span>
                    <span className="tabular" style={{ color: 'var(--hj-beige-ink)', fontWeight: 600, fontFamily: 'var(--hj-font-mono)' }}>
                      {formatRate(profile.reader_director_rate)}
                    </span>
                  </div>
                )}
                {profile?.reader_coach_rate != null && (
                  <div className="flex justify-between" style={{ fontSize: 13 }}>
                    <span style={{ color: 'var(--hj-beige-ink-3)' }}>Coach</span>
                    <span className="tabular" style={{ color: 'var(--hj-beige-ink)', fontWeight: 600, fontFamily: 'var(--hj-font-mono)' }}>
                      {formatRate(profile.reader_coach_rate)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Categories */}
          {profile?.reader_categories && profile.reader_categories.length > 0 && (
            <div>
              <div className="mono-label" style={{ marginBottom: 6 }}>Categories & styles</div>
              <div className="flex flex-wrap gap-1.5">
                {profile.reader_categories.map((cat) => (
                  <span
                    key={cat}
                    style={{
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: 'var(--hj-beige-deep)',
                      border: '1px solid var(--hj-beige-line)',
                      fontSize: 11.5,
                      color: 'var(--hj-beige-ink-2)',
                    }}
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          )}

          {profile?.reader_experience && (
            <div>
              <div className="mono-label" style={{ marginBottom: 6 }}>About</div>
              <p
                className="whitespace-pre-line m-0"
                style={{ fontSize: 13.5, color: 'var(--hj-beige-ink)', lineHeight: 1.5 }}
              >
                {profile.reader_experience}
              </p>
            </div>
          )}

          {profile?.reader_availability_notes && (
            <div>
              <div className="mono-label" style={{ marginBottom: 6 }}>Availability notes</div>
              <p
                className="m-0"
                style={{ fontSize: 13.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.5 }}
              >
                {profile.reader_availability_notes}
              </p>
            </div>
          )}

          {/* The old "pending invites / bookings confirmed" panels read casting's
              freelancer_requests, which no longer see anything booked on Plus.
              Your sessions with this reader live on /readers/requests. */}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => { onClose(); onInvite(reader); }}>
            <CalendarDays className="w-4 h-4 mr-1" /> Book a session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
